import { msg } from "@lingui/macro";
import {
  addDays,
  addMonths,
  format,
  isAfter,
  isBefore,
  parseISO,
} from "date-fns";

import { RecurrenceFormValue } from "./types";

export type RecurrenceType = "DAY" | "WEEK" | "MONTH";

type RecurrenceSchedule = {
  recurrenceType: RecurrenceType;
  repeatInterval: number;
  endCondition: "ON_DATE" | "AFTER_OCCURRENCES";
  endDate?: string;
};

type DatedSeriesBooking = {
  startSlot?: {
    agenda?: {
      date?: string | null;
    } | null;
  } | null;
};

const occurrenceDate = (
  sourceDate: string,
  recurrenceType: RecurrenceType,
  repeatInterval: number,
  occurrenceIndex: number
) =>
  recurrenceType === "MONTH"
    ? addMonths(parseISO(sourceDate), repeatInterval * occurrenceIndex)
    : addDays(
        parseISO(sourceDate),
        (recurrenceType === "WEEK" ? repeatInterval * 7 : repeatInterval) *
          occurrenceIndex
      );

export const nextOccurrenceDateAfter = (
  sourceDate: string,
  selectedOccurrenceDate: string,
  recurrenceType: RecurrenceType,
  repeatInterval: number
) => {
  const selected = parseISO(selectedOccurrenceDate);

  let occurrenceIndex = 0;
  let occurrence = occurrenceDate(
    sourceDate,
    recurrenceType,
    repeatInterval,
    occurrenceIndex
  );

  while (isBefore(occurrence, selected)) {
    occurrenceIndex += 1;
    occurrence = occurrenceDate(
      sourceDate,
      recurrenceType,
      repeatInterval,
      occurrenceIndex
    );
  }

  if (isAfter(occurrence, selected)) {
    return occurrence;
  }

  return occurrenceDate(
    sourceDate,
    recurrenceType,
    repeatInterval,
    occurrenceIndex + 1
  );
};

export const getMaximumOccurrenceCount = ({
  sourceDate,
  maximumEndDate,
  recurrenceType,
  repeatInterval,
}: {
  sourceDate: string;
  maximumEndDate: string;
  recurrenceType: RecurrenceType;
  repeatInterval: number;
}) => {
  return remainingOccurrenceCount(
    sourceDate,
    maximumEndDate,
    recurrenceType,
    repeatInterval
  );
};

export const remainingOccurrenceCountFromAnchor = (
  sourceDate: string,
  selectedOccurrenceDate: string,
  effectiveEndDate: string,
  recurrenceType: RecurrenceType,
  repeatInterval: number
) => {
  const selected = parseISO(selectedOccurrenceDate);
  const maximum = parseISO(effectiveEndDate);
  let count = isAfter(selected, maximum) ? 0 : 1;
  let occurrenceIndex = 0;
  let occurrence = occurrenceDate(
    sourceDate,
    recurrenceType,
    repeatInterval,
    occurrenceIndex
  );

  // Locate the selected cadence position before applying the 366-occurrence
  // limit. A long-lived daily series can have more than 366 historical dates
  // between its anchor and the occurrence being edited.
  while (isBefore(occurrence, selected)) {
    occurrenceIndex += 1;
    occurrence = occurrenceDate(
      sourceDate,
      recurrenceType,
      repeatInterval,
      occurrenceIndex
    );
  }

  while (count < 366 && !isAfter(occurrence, maximum)) {
    if (isAfter(occurrence, selected)) count += 1;
    occurrenceIndex += 1;
    occurrence = occurrenceDate(
      sourceDate,
      recurrenceType,
      repeatInterval,
      occurrenceIndex
    );
  }

  return count;
};

export const remainingOccurrenceCount = (
  selectedOccurrenceDate: string,
  effectiveEndDate: string,
  recurrenceType: RecurrenceType,
  repeatInterval: number
) =>
  remainingOccurrenceCountFromAnchor(
    selectedOccurrenceDate,
    selectedOccurrenceDate,
    effectiveEndDate,
    recurrenceType,
    repeatInterval
  );

export type PersistedSeriesRecurrence = {
  recurrenceType?: RecurrenceType | null;
  repeatInterval?: number | null;
  endCondition?: "ON_DATE" | "AFTER_OCCURRENCES" | null;
  effectiveEndDate?: string | null;
  requestedOccurrenceCount?: number | null;
};

export const recurrenceInputFromPersistedSeries = (
  series: PersistedSeriesRecurrence | null | undefined,
  selectedOccurrenceDate?: string | null
): RecurrenceFormValue | null => {
  if (!series?.recurrenceType) return null;

  const recurrenceType = series.recurrenceType;
  const repeatInterval = series.repeatInterval ?? 1;

  if (series.endCondition === "ON_DATE") {
    return series.effectiveEndDate
      ? {
          recurrenceType,
          repeatInterval,
          endCondition: "ON_DATE",
          endDate: series.effectiveEndDate,
        }
      : null;
  }

  return {
    recurrenceType,
    repeatInterval,
    endCondition: "AFTER_OCCURRENCES",
    occurrenceCount:
      series.effectiveEndDate && selectedOccurrenceDate
        ? remainingOccurrenceCount(
            selectedOccurrenceDate,
            series.effectiveEndDate,
            recurrenceType,
            repeatInterval
          )
        : series.requestedOccurrenceCount ?? 1,
  };
};

export const recurrenceEndDate = (
  sourceDate: string,
  value: RecurrenceSchedule,
  initialValue?: RecurrenceSchedule,
  selectedOccurrenceDate = sourceDate
) => {
  const firstFutureOccurrenceDate = format(
    nextOccurrenceDateAfter(
      sourceDate,
      selectedOccurrenceDate,
      value.recurrenceType,
      value.repeatInterval
    ),
    "yyyy-MM-dd"
  );
  const keepsExistingOnDateSchedule =
    initialValue?.endCondition === "ON_DATE" &&
    value.endCondition === "ON_DATE" &&
    value.endDate === initialValue.endDate &&
    value.recurrenceType === initialValue.recurrenceType &&
    value.repeatInterval === initialValue.repeatInterval;

  return value.endDate &&
    (keepsExistingOnDateSchedule ||
      !isAfter(parseISO(firstFutureOccurrenceDate), parseISO(value.endDate)))
    ? value.endDate
    : firstFutureOccurrenceDate;
};

export const sortSeriesBookingsByDate = <Booking extends DatedSeriesBooking>(
  bookings: Booking[]
) =>
  [...bookings].sort((left, right) =>
    (left.startSlot?.agenda?.date ?? "").localeCompare(
      right.startSlot?.agenda?.date ?? ""
    )
  );

export const lastSeriesOccurrenceDate = ({
  sourceDate,
  recurrenceType,
  repeatInterval,
  occurrenceCount,
}: {
  sourceDate: string;
  recurrenceType: RecurrenceType;
  repeatInterval: number;
  occurrenceCount: number;
}) =>
  format(
    occurrenceDate(
      sourceDate,
      recurrenceType,
      repeatInterval,
      Math.max(occurrenceCount - 1, 0)
    ),
    "yyyy-MM-dd"
  );

export const cadenceOptions = [
  { label: msg`Daily`, value: "daily" },
  { label: msg`Weekly`, value: "weekly" },
  { label: msg`Every other week`, value: "every-other-week" },
  { label: msg`Monthly`, value: "monthly" },
] as const;

export const getRecurrenceForCadence = (value: string) =>
  value === "daily"
    ? { recurrenceType: "DAY" as const, repeatInterval: 1 }
    : value === "monthly"
    ? { recurrenceType: "MONTH" as const, repeatInterval: 1 }
    : value === "every-other-week"
    ? { recurrenceType: "WEEK" as const, repeatInterval: 2 }
    : { recurrenceType: "WEEK" as const, repeatInterval: 1 };

export const getAvailableRecurrenceCadenceValues = ({
  maximumEndDate,
  sourceDate,
}: {
  maximumEndDate: string;
  sourceDate: string;
}) =>
  cadenceOptions
    .filter((option) => {
      const candidate = getRecurrenceForCadence(option.value);

      return (
        getMaximumOccurrenceCount({
          maximumEndDate,
          ...candidate,
          sourceDate,
        }) >= 2
      );
    })
    .map((option) => option.value);

export const cadenceValueForRecurrence = ({
  recurrenceType,
  repeatInterval,
}: {
  recurrenceType: RecurrenceType;
  repeatInterval: number;
}) =>
  recurrenceType === "DAY"
    ? "daily"
    : recurrenceType === "MONTH"
    ? "monthly"
    : repeatInterval === 2
    ? "every-other-week"
    : "weekly";
